/* eslint-disable no-param-reassign */

/**
 * Creates a deep copy of an object using lodash/cloneDeep.
 * @constant
 * @type {Function}
 * @see {@link https://lodash.com/docs/4.17.15#cloneDeep}
*/
import { cloneDeep } from 'lodash-es';
import {
  elementsState,
  elementsActions,
  elementsGetters,
  elementsMutations,
} from './elements';

import createGettersFactory from '../../utils/createGettersFactory';

// NOTE: Anything you change here can effect backend validation
// see - templateData in routes/templates.js

/**
 * Module representing the template state.
 * @module templateState
*/

/**
 * Creates the root state object.
 * @function rootState
 * @returns {object} The root state object.
*/
const templateRootState = () => ({
  version: 'v1.3',
  owner: null,
  _id: null,
  title: 'Untitled',
  snippets: [],
  tags: [],
  template_id: null,

  // find out more about snippets and how they are used
  // its not bootstrapped in the template defaults
  // but it shows up with saved stuff
});

/**
 * Represents the guides state.
 * @typedef {object} GuidesState
 * @property {boolean} show - Flag indicating whether guides are shown.
 * @property {array} hGuides - Horizontal guides.
 * @property {array} vGuides - Vertical guides.
*/
const guidesState = {
  show: false,
  hGuides: [],
  vGuides: [],
};

/**
 * Represents the safe area state.
 * @typedef {object} SafeAreaState
 * @property {boolean} show - Flag indicating whether safe area is shown.
 * @property {number} width - Safe area width.
 * @property {number} height - Safe area height.
*/
const safeAreaState = {
  show: false,
  width: 90,
  height: 90,
};

/**
 * Represents the canvas state.
 * @typedef {object} CanvasState
 * @property {number} width - Canvas width.
 * @property {number} height - Canvas height.
*/
const canvasState = {
  width: 1920,
  height: 1080,
};

/**
 * Root getters object.
 * @type {object}
*/
const rootGetters = {
  getTemplate(state) {
    return state;
  },
  getCanvas(state) {
    return state.canvas;
  },
  getSafeArea(state) {
    return state.safeArea;
  },
  getGuides(state) {
    return state.guides;
  },
  getElements(state) {
    return state.elements.elementsById;
  },
  getElementGroupMap(state) {
    return state.elements.elementGroupMap;
  },
};

/**
 * Root actions object.
 * @type {object}
*/
const rootActions = {
  /**
   * Sets the entire template state.
   * @function setAllGraphicsTemplateState
   * @param {object} context - The context object.
   * @param {object} payload - The payload object.
   * @param {object} payload.fullTemplate - The full template object.
   * @param {object} payload.options - Rough-in options for future use.
   */
  setAllGraphicsTemplateState({ dispatch }, payload) {
    const { fullTemplate, options } = payload; // options rough in for use later in undo/redo work
    const {
      elements,
      canvas,
      safeArea,
      guides,
      // eslint-disable-next-line camelcase
      created_by,
      // eslint-disable-next-line camelcase
      created_time,
      ...rootProps
    } = fullTemplate;

    dispatch('setElementStoreValues', { value: elements, options });
    dispatch('setCanvasStateData', { value: canvas, options });
    dispatch('setSafeAreaStateData', { value: safeArea, options });
    dispatch('setGuidesStateData', { value: guides, options });
    dispatch('setRootStateData', { value: rootProps, options });
  },

  /**
   * Sets the root state data.
   * @function setRootStateData
   * @param {object} commit - The commit object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
  */
  setRootStateData({ commit }, payload) {
    commit('SET_TEMPLATE_ROOT_STATE_DATA', payload);
  },
  /**
   * Transforms the graphics template state for saving.
   *
   * @param {Object} context - The context object.
   * @param {Object} context.getters - The getters object.
   * @param {Object} context.rootState - The root state object.
   * @returns {Object} - The transformed graphics template state.
   */
  transformGraphicsTemplateStateForSave({ getters, rootState }) {
    const { elements, ...restProps } = cloneDeep(rootState.graphicsTemplate);
    return { ...restProps, elements: getters.getNestedElements };
  },
  /**
   * Sets the template title state data.
   * @function setTemplateTitle
   * @param {object} commit - The commit object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
   */
  setTemplateTitle({ commit }, payload) {
    commit('SET_TEMPLATE_TITLE', payload);
  },
  /**
   * Sets the template id state data.
   * @function setTemplateId
   * @param {object} commit - The commit object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
   */
  setTemplateId({ commit }, payload) {
    commit('SET_TEMPLATE_ID', payload);
  },
  setTemplateTemplateId({ commit }, payload) {
    commit('SET_TEMPLATE_TEMPLATE_ID', payload);
  },
  addTemplateTag({ commit }, payload) {
    commit('ADD_TEMPLATE_TAG', payload);
  },
  removeTemplateTag({ commit }, payload) {
    commit('REMOVE_TEMPLATE_TAG', payload);
  },
  addTemplateSnippet({ commit }, payload) {
    commit('ADD_TEMPLATE_SNIPPET', payload);
  },
  removeTemplateSnippet({ commit }, payload) {
    commit('REMOVE_TEMPLATE_SNIPPET', payload);
  },
  setTemplateOwner({ commit }, payload) {
    commit('SET_TEMPLATE_OWNER', payload);
  },
};

/**
 * Root mutations object.
 * @type {object}
 */
const rootMutations = {
  /**
   * Sets the template root state data.
   * @function SET_TEMPLATE_ROOT_STATE_DATA
   * @param {object} state - The root state object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
   */
  SET_TEMPLATE_ROOT_STATE_DATA(state, payload) {
    const { value, options } = payload;

    // make sure new props go into existing productions
    const { version, ...restRootState } = templateRootState();
    const combined = { restRootState, ...value, version };
    Object.assign(state, combined);
  },
  /**
   * Resets the root state.
   * @function RESET_ROOT_STATE
   * @param {object} state - The root state object.
   * @param {object} payload - The payload object.
   * @param {object} payload.options - The options object.
   */
  RESET_ROOT_STATE(state, payload) {
    const { options } = payload;
    Object.assign(state, ...templateRootState());
  },
  /**
   * Set template title value by property.
   * Sets the value of a template title property.
   * @param {Object} state - The root state object.
   * @param {Object} payload - The payload containing the title value.
   * @param {String} payload.value - The name of the template.
   */
  SET_TEMPLATE_TITLE(state, payload) {
    const { value, options } = payload;
    state.title = value;
  },
  /**
   * Set template id value by property.
   * Sets the value of a template id property.
   * @param {Object} state - The root state object.
   * @param {Object} payload - The payload containing the id value.
   * @param {String} payload.value - The name of the template.
   */
  SET_TEMPLATE_ID(state, payload) {
    const { value, options } = payload;
    state._id = value;
  },
  SET_TEMPLATE_TEMPLATE_ID(state, payload) {
    const { value } = payload;
    state.template_id = value;
  },
  ADD_TEMPLATE_TAG(state, payload) {
    const { value, options } = payload;
    state.tags.push(value);
  },
  REMOVE_TEMPLATE_TAG(state, payload) {
    const { value, options } = payload;
    const index = state.tags.indexOf(value);
    state.tags.splice(index, 1);
  },
  ADD_TEMPLATE_SNIPPET(state, payload) {
    const { value, options } = payload;
    state.snippets.push(value);
  },
  REMOVE_TEMPLATE_SNIPPET(state, payload) {
    const { value, options } = payload;
    const index = state.snippets.indexOf(value);
    state.snippets.splice(index, 1);
  },
  /**
   * Set template owner value by property.
   * Sets the value of a template owner property.
   * @param {Object} state - The root state object.
   * @param {Object} payload - The payload containing the owner value.
   * @param {String} payload.value - The name of the template.
   */
  SET_TEMPLATE_OWNER(state, payload) {
    const { value } = payload;
    state.owner = +value;
  },
};

/**
 * Canvas getters object.
@type {object}
*/
const canvasGetters = {};

/**
 * Canvas actions object.
@type {object}
*/
const canvasActions = {
  /**
   * Sets the canvas state data.
   * @function setCanvasStateData
   * @param {object} commit - The commit object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
   */
  setCanvasStateData({ commit }, payload) {
    commit('SET_CANVAS_STATE_DATA', payload);
  },
  setCanvasWidth({ commit }, payload) {
    const commitPayload = {
      value: { propKey: 'width', propValue: payload.value },
    };
    commit('SET_CANVAS_VALUE_BY_PROP', commitPayload);
  },
  setCanvasHeight({ commit }, payload) {
    const commitPayload = {
      value: { propKey: 'height', propValue: payload.value },
    };
    commit('SET_CANVAS_VALUE_BY_PROP', commitPayload);
  },
};

/**
 * Canvas mutations object.
@type {object}
*/
const canvasMutations = {
  // we will be using mutation payload to set some undo/redo flags,
  // so we always want to make an object with value being the traditional payload

  /**
   * Sets the canvas state data.
   * @function SET_CANVAS_STATE_DATA
   * @param {object} state - The canvas state object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
  */
  SET_CANVAS_STATE_DATA(state, payload) {
    const { value, options } = payload;
    // make sure new props go into existing productions
    const combined = { ...canvasState, ...value };
    Object.assign(state, combined);
  },
  /**
   * Set canvas value by property.
   * Sets the value of a canvas property.
   * @param {Object} context - The context object.
   * @param {Object} payload - The payload containing the element ID, property key, and property value.
   * @param {Object} payload.value - The element ID, property key, and property value object.
   */
  SET_CANVAS_VALUE_BY_PROP(state, payload) {
    const { value, options } = payload;
    const { propKey, propValue } = value;
    state[propKey] = propValue;
  },
};

/**
 * Safe-area getters object.
 * @type {object}
*/
const safeAreaGetters = {};

/**
 * Safe-area actions object.
 * @type {object}
*/
const safeAreaActions = {
  /**
   * Sets the safe area state data.
   * @function setSafeAreaStateData
   * @param {object} state - The safe area state object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
  */
  setSafeAreaStateData({ commit }, payload) {
    commit('SET_SAFE_AREA_STATE_DATA', payload);
  },
  setSafeAreaWidth({ commit }, payload) {
    const commitPayload = {
      value: { propKey: 'width', propValue: payload.value },
    };
    commit('SET_SAFE_AREA_VALUE_BY_PROP', commitPayload);
  },
  setSafeAreaHeight({ commit }, payload) {
    const commitPayload = {
      value: { propKey: 'height', propValue: payload.value },
    };
    commit('SET_SAFE_AREA_VALUE_BY_PROP', commitPayload);
  },
  setSafeAreaShow({ commit }, payload) {
    const commitPayload = {
      value: { propKey: 'show', propValue: payload.value },
    };
    commit('SET_SAFE_AREA_VALUE_BY_PROP', commitPayload);
  },
};

/**
 * Safe-area mutations object.
 * @type {object}
*/
const safeAreaMutations = {
  // we will be using mutation payload to set some undo/redo flags,
  // so we always want to make an object with value being the traditional payload
  /**
   * Sets the safe area state data.
   * @function SET_SAFE_AREA_STATE_DATA
   * @param {object} state - The safe area state object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
  */
  SET_SAFE_AREA_STATE_DATA(state, payload) {
    const { value, options } = payload;
    // make sure new props go into existing productions
    const combined = { ...safeAreaState, ...value };
    Object.assign(state, combined);
  },
  /**
   * Set safe area value by property.
   * Sets the value of a safe area property.
   * @param {Object} context - The context object.
   * @param {Object} payload - The payload containing the element ID, property key, and property value.
   * @param {Object} payload.value - The element ID, property key, and property value object.
   */
  SET_SAFE_AREA_VALUE_BY_PROP(state, payload) {
    const { value, options } = payload;
    const { propKey, propValue } = value;
    state[propKey] = propValue;
  },
};

/**
 * Guides getters object.
 * @type {object}
*/
const guidesGetters = {};

/**
 * Guides actions object.
 * @type {object}
*/
const guidesActions = {
  /**
   * Sets the guides state data.
   * @function setGuidesStateData
   * @param {object} state - The guides state object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
  */
  setGuidesStateData({ commit }, payload) {
    commit('SET_GUIDES_STATE_DATA', payload);
  },
  setGuideHorizontal({ commit }, payload) {
    const commitPayload = {
      value: { propKey: 'hGuides', propValue: payload.value },
    };
    commit('SET_GUIDES_VALUE_BY_PROP', commitPayload);
  },
  setGuideVertical({ commit }, payload) {
    const commitPayload = {
      value: { propKey: 'vGuides', propValue: payload.value },
    };
    commit('SET_GUIDES_VALUE_BY_PROP', commitPayload);
  },
  setGuidesShow({ commit }, payload) {
    const commitPayload = {
      value: { propKey: 'show', propValue: payload.value },
    };
    commit('SET_GUIDES_VALUE_BY_PROP', commitPayload);
  },
  clearGuides({ commit }) {
    commit('SET_GUIDES_VALUE_BY_PROP', { value: { propKey: 'vGuides', propValue: [] } });
    commit('SET_GUIDES_VALUE_BY_PROP', { value: { propKey: 'hGuides', propValue: [] } });
  },
};

/**
 * Guides mutations object.
 * @type {object}
*/
const guidesMutations = {
  // we will be using mutation payload to set some undo/redo flags,
  // so we always want to make an object with value being the traditional payload
  /**
   * Sets the guides state data.
   * @function SET_GUIDES_STATE_DATA
   * @param {object} state - The guides state object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
  */
  SET_GUIDES_STATE_DATA(state, payload) {
    const { value, options } = payload;
    // make sure new props go into existing productions
    const combined = { ...guidesState, ...value };
    Object.assign(state, combined);
  },
  /**
   * Set element value by property.
   * Sets the value of an element property.
   * @param {Object} context - The context object.
   * @param {Object} payload - The payload containing the element ID, property key, and property value.
   * @param {Object} payload.value - The element ID, property key, and property value object.
   */
  SET_GUIDES_VALUE_BY_PROP(state, payload) {
    const { value, options } = payload;
    const { propKey, propValue } = value;
    state[propKey] = propValue;
  },
};

/**
* The template module.
 * @type {TemplateModule}
*/
export default {
  namespaced: true,

  // module assets
  state: templateRootState,
  getters: { ...createGettersFactory(templateRootState(), 'Template'), ...rootGetters },
  actions: rootActions,
  mutations: rootMutations,

  // nested modules
  modules: {
    // inherits the namespace from parent module
    canvas: {
      state: canvasState,
      getters: canvasGetters,
      actions: canvasActions,
      mutations: canvasMutations,
    },
    safeArea: {
      state: safeAreaState,
      getters: safeAreaGetters,
      actions: safeAreaActions,
      mutations: safeAreaMutations,
    },
    guides: {
      state: guidesState,
      getters: guidesGetters,
      actions: guidesActions,
      mutations: guidesMutations,
    },
    elements: {
      state: elementsState,
      getters: elementsGetters,
      actions: elementsActions,
      mutations: elementsMutations,
    },
  },
};
